// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '../config/firebase'; // Import db from firebase config
import RecencyService from '../services/RecencyService';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecencyWarning, setShowRecencyWarning] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Auth methods
  const signup = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification
      if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
        console.log("Email verification sent!");
      }

      return userCredential.user;
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Debug log
      console.log('Login credential:', userCredential);

      // Ensure user has verified their email before proceeding
      if (!userCredential.user.emailVerified) {
        throw new Error('Please verify your email before logging in.');
      }

      // Return the entire userCredential object
      return userCredential;
    } catch (error) {
      console.error("Login error in AuthContext:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Wait for token to be refreshed
          await user.getIdToken(true);
          setUser(user);
          setIsAuthenticated(true);
          
          try {
            const { hasRecentFlights } = await RecencyService.calculateRecency(user.uid);
            if (!hasRecentFlights) {
              setShowRecencyWarning(true);
            }
          } catch (error) {
            console.error('Error checking flight recency:', error);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Add a loading state component
  if (loading) {
    return (
      <AuthContext.Provider value={{ user: null, loading: true, error: null, db: null }}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </AuthContext.Provider>
    );
  }

  const value = {
    user,
    loading,
    error,
    signup,
    login,
    logout,
    resetPassword,
    db, // Include the Firestore instance in the context
    showRecencyWarning,
    setShowRecencyWarning,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;